import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Image,
  Button,
  Heading,
  VStack,
  Container,
  Text,
  Flex,
  useBreakpointValue,
  HStack,
} from "@chakra-ui/react";

// --- KLM palette -----------------------------------------------------
const PAPER = "#F5F7F4";
const INK = "#0A2540";
const NAVY = "#061A2C"; // deep KLM ink-blue, page background
const KLM_BLUE = "#00A1E4"; // signature KLM blue -> Economy
const KLM_BLUE_HOVER = "#0088C4";
const PREMIUM = "#C9A15A"; // warm brass -> Premium Comfort
const PREMIUM_HOVER = "#B38B44";
const BUSINESS = "#5B4B8A"; // indigo -> World Business Class
const BUSINESS_HOVER = "#493B70";

const ORIGIN = "BUH";
const BOOK_URL = "https://tbs.flytime.ro/reseller/auth/";

const BANNER_IMAGE = `/images/af-kl/KLM-header.png`;
const LOGO_IMAGE = `https://travel-time.ro/images/af-kl/klm-logo-white.png`;

// TODO: înlocuiește prețurile și imaginile cu cele reale
const cabinGroups = [
  {
    key: "economy",
    label: "Economy",
    accent: KLM_BLUE,
    accentHover: KLM_BLUE_HOVER,
    travelPeriod:
      "NATL: 17 sep 2026 – 31 mar 2027 · ICA: 3 sep 2026 – 30 iun 2027",
    destinations: [
      {
        code: "LAS",
        city: "Las Vegas",
        region: "Nevada, SUA",
        price: "791",
        image: `https://travel-time.ro/images/af-kl/KLPremium/las-vegas.jpg`,
      },
      {
        code: "CTG",
        city: "Cartagena",
        region: "Columbia",
        price: "729",
        image: `https://travel-time.ro/images/af-kl/kl-sep-2025/Cartagena.jpg`,
      },
      {
        code: "YTO",
        city: "Toronto",
        region: "Canada",
        price: "695",
        image: `https://travel-time.ro/images/af-kl/kl-sep-2025/Toronto.jpg`,
      },
      {
        code: "KUL",
        city: "Kuala Lumpur",
        region: "Malaezia",
        price: "704",
        image: `https://travel-time.ro/images/af-kl/KLPremium/kuala-lumpur.jpg`,
      },
    ],
  },
  {
    key: "premium",
    label: "Premium Comfort",
    accent: PREMIUM,
    accentHover: PREMIUM_HOVER,
    travelPeriod: "ICA: 3 sep 2026 – 30 iun 2027",
    destinations: [
      {
        code: "NYC",
        city: "New York",
        region: "SUA",
        price: "1438",
        image: `https://travel-time.ro/images/af-kl/kl-sep-2025/New York.jpg`,
      },
      {
        code: "YMQ",
        city: "Montreal",
        region: "Canada",
        price: "1526",
        image: `https://travel-time.ro/images/af-kl/af-sep-2025/montreal.jpg`,
      },
      {
        code: "UIO",
        city: "Quito",
        region: "Ecuador",
        price: "1669",
        image: `https://travel-time.ro/images/af-kl/KLPremium/Quito%20Church.jpg`,
      },
      {
        code: "CPT",
        city: "Cape Town",
        region: "Africa de Sud",
        price: "1341",
        image: `https://travel-time.ro/images/af-kl/kl-sep-2025/cape-town.jpg`,
      },
    ],
  },
  {
    key: "business",
    label: "World Business Class",
    accent: BUSINESS,
    accentHover: BUSINESS_HOVER,
    travelPeriod: "17 sep 2026 – 30 iun 2027",
    destinations: [
      {
        code: "CUN",
        city: "Cancún",
        region: "Mexic",
        price: "3163",
        image: `https://travel-time.ro/images/af-kl/KLPremium/Cancun.jpg`,
      },
      {
        code: "YVR",
        city: "Vancouver",
        region: "Canada",
        price: "3228",
        image: `https://travel-time.ro/images/af-kl/kl-mar-2026/vancouver.jpg`,
      },
      {
        code: "AUA",
        city: "Aruba",
        region: "Caraibe",
        price: "3412",
        image: `https://travel-time.ro/images/af-kl/KLEconomic/Aruba.jpg`,
      },
      {
        code: "LIM",
        city: "Lima",
        region: "Peru",
        price: "3489",
        image: `https://travel-time.ro/images/af-kl/af-sep-2025/Lima.jpg`,
      },
    ],
  },
];

const TicketCard = ({ d, index, groupIndex, accent, accentHover, mounted }) => {
  const [isHovered, setIsHovered] = useState(false);
  const photoH = useBreakpointValue({
    base: "260px",
    sm: "300px",
    lg: "320px",
  });
  const delay = groupIndex * 0.05 + index * 0.06;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      opacity={mounted ? 1 : 0}
      transform={mounted ? "translateY(0)" : "translateY(18px)"}
      transition={`opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`}
    >
      <Box
        position="relative"
        bg={PAPER}
        rounded="lg"
        overflow="hidden"
        shadow={
          isHovered
            ? "0 20px 40px rgba(0,0,0,0.35)"
            : "0 8px 20px rgba(0,0,0,0.2)"
        }
        transform={isHovered ? "translateY(-4px)" : "translateY(0)"}
        transition="all 0.3s ease"
      >
        {/* Photo panel */}
        <Box position="relative" h={photoH} overflow="hidden">
          <Image
            src={d.image}
            alt={d.city}
            w="full"
            h="full"
            objectFit="cover"
            objectPosition="center"
          />
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(to-t, blackAlpha.800 15%, blackAlpha.100 60%)"
          />

          <Box position="absolute" top={0} left={0} right={0} px={4} pt={3}>
            <HStack justify="space-between">
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="xs"
                color="whiteAlpha.700"
                letterSpacing="0.08em"
              >
                {d.group}
              </Text>
            </HStack>
          </Box>

          <Box position="absolute" bottom={0} left={0} right={0} px={4} pb={3}>
            <Flex align="center" position="relative" h="20px">
              <Text
                fontFamily="'Inter', sans-serif"
                fontWeight="700"
                fontSize="md"
                color={PAPER}
              >
                {ORIGIN}
              </Text>
              <Box
                flex="1"
                mx={3}
                position="relative"
                h="1px"
                bg="whiteAlpha.500"
              >
                <Box
                  position="absolute"
                  top="-7px"
                  fontSize="14px"
                  left={isHovered ? "88%" : "6%"}
                  opacity={isHovered ? 1 : 0.7}
                  transition="left 1.1s ease, opacity 0.3s ease"
                >
                  ✈
                </Box>
              </Box>
              <Text
                fontFamily="'Inter', sans-serif"
                fontWeight="700"
                fontSize="md"
                color={PAPER}
              >
                {d.code}
              </Text>
            </Flex>
          </Box>
        </Box>

        {/* Perforation */}
        <Box position="relative" h="0">
          <Box
            position="absolute"
            left="-10px"
            top="-10px"
            w="20px"
            h="20px"
            bg={NAVY}
            rounded="full"
          />
          <Box
            position="absolute"
            right="-10px"
            top="-10px"
            w="20px"
            h="20px"
            bg={NAVY}
            rounded="full"
          />
          <Box
            position="absolute"
            left="24px"
            right="24px"
            top="-1px"
            borderTop="1.5px dashed"
            borderColor="blackAlpha.300"
          />
        </Box>

        {/* City info */}
        <Flex align="flex-start" justify="space-between" px={6} pt={6} pb={5}>
          <VStack align="flex-start" spacing={1}>
            <Heading
              fontFamily="'Fraunces', serif"
              fontWeight="500"
              fontSize="3xl"
              color={INK}
            >
              {d.city}
            </Heading>
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="sm"
              color="blackAlpha.600"
            >
              {d.region}
            </Text>
          </VStack>
          <VStack align="flex-end" spacing={0} flexShrink={0} pl={2}>
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="xs"
              color="blackAlpha.500"
            >
              de la
            </Text>
            <Text
              fontFamily="'Fraunces', serif"
              fontWeight="600"
              fontSize="2xl"
              color={accent}
            >
              {d.price} €
            </Text>
          </VStack>
        </Flex>

        {/* CTA */}
        <Flex align="center" justify="space-between" px={6} pb={6} pt={2}>
          <Button
            as="a"
            href={BOOK_URL}
            size="sm"
            bg={accent}
            color="white"
            rounded="full"
            px={5}
            fontFamily="'Inter', sans-serif"
            fontWeight="600"
            _hover={{ bg: accentHover }}
          >
            Rezervă
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export const GridPromoKlmSEP26 = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  // toate cele 4 destinații pe un rând, dar carduri mai late
  const columns = useBreakpointValue({ base: 1, sm: 2, lg: 4 });
  const bannerH = useBreakpointValue({ base: "260px", md: "460px" });

  return (
    <Box bg={NAVY} minH="100vh" position="relative" overflow="hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');
      `}</style>

      {/* Hero banner — centered, 75% width, equal side margins */}
      <Box pt={{ base: 6, md: 10 }} px={{ base: "8%", md: "8%" }}>
        <Box
          position="relative"
          h={bannerH}
          w="full"
          overflow="hidden"
          rounded="xl"
        >
          <Image
            src={BANNER_IMAGE}
            alt="Zboruri KLM din București"
            w="full"
            h="full"
            objectFit="cover"
          />
          {/* light bottom scrim, just enough for the logo/wordmark to read */}
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(to-t, rgba(6,26,44,0.65) 0%, rgba(6,26,44,0.0) 38%)"
          />

          <Image
            src={LOGO_IMAGE}
            alt="KLM"
            position="absolute"
            top={{ base: 5, md: 7 }}
            left={{ base: 5, md: 7 }}
            h={{ base: "48px", md: "76px" }}
            objectFit="contain"
          />

          <Flex
            direction="column"
            justify="flex-end"
            position="absolute"
            inset={0}
            h="full"
            p={{ base: 5, md: 8 }}
          >
            <Text
              fontFamily="'Inter', sans-serif"
              fontWeight="600"
              fontSize="sm"
              color={KLM_BLUE}
              letterSpacing="0.04em"
              mb={2}
            >
              8 – 28 septembrie 2026
            </Text>
          </Flex>
        </Box>
      </Box>

      {/* faint dotted route-map texture over the rest of the page */}
      <Box
        position="absolute"
        top={bannerH}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        backgroundImage="radial-gradient(circle, #ffffff 1px, transparent 1px)"
        backgroundSize="28px 28px"
        pointerEvents="none"
      />

      <Container
        maxW="9xl"
        position="relative"
        py={{ base: 10, md: 14 }}
        px={{ base: 5, md: 10 }}
      >
        <HStack
          spacing={3}
          border="1px solid"
          borderColor="whiteAlpha.300"
          rounded="full"
          px={4}
          py={2}
          w="fit-content"
          mb={{ base: 10, md: 12 }}
        >
          <Box
            w="6px"
            h="6px"
            rounded="full"
            bg={KLM_BLUE}
            border="1px solid"
            borderColor="whiteAlpha.600"
          />
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="sm"
            color="whiteAlpha.800"
          >
            Economy · Premium Comfort · World Business Class — plecare din{" "}
            {ORIGIN}
          </Text>
        </HStack>

        <VStack align="stretch" spacing={{ base: 12, md: 16 }}>
          {cabinGroups.map((group, gi) => (
            <Box key={group.key}>
              <Flex
                align={{ base: "flex-start", sm: "center" }}
                justify="space-between"
                direction={{ base: "column", sm: "row" }}
                gap={2}
                mb={{ base: 5, md: 6 }}
                pb={4}
                borderBottom="1px solid"
                borderColor="whiteAlpha.200"
              >
                <HStack spacing={3}>
                  <Box w="10px" h="10px" rounded="full" bg={group.accent} />
                  <Heading
                    fontFamily="'Fraunces', serif"
                    fontWeight="500"
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color={PAPER}
                  >
                    {group.label}
                  </Heading>
                </HStack>
                <Text
                  fontFamily="'Inter', sans-serif"
                  fontSize="sm"
                  color="whiteAlpha.600"
                >
                  Perioadă de călătorie: {group.travelPeriod}
                </Text>
              </Flex>

              <Grid
                templateColumns={`repeat(${columns}, 1fr)`}
                gap={{ base: 6, md: 6 }}
              >
                {group.destinations.map((d, i) => (
                  <TicketCard
                    key={d.code}
                    d={d}
                    index={i}
                    groupIndex={gi}
                    accent={group.accent}
                    accentHover={group.accentHover}
                    mounted={mounted}
                  />
                ))}
              </Grid>
            </Box>
          ))}
        </VStack>

        <Text
          fontFamily="'Inter', sans-serif"
          fontSize="md"
          color="whiteAlpha.500"
          mt={{ base: 12, md: 14 }}
          maxW="3xl"
        >
          NATL = rute nord-atlantice (SUA/Canada); ICA = rute intercontinentale
          (America Latină, Africa, Asia). Perioada de călătorie pentru World
          Business Class pe rutele ICA începe pe 17 septembrie 2026. Prețurile
          sunt orientative și pot varia în funcție de dată și disponibilitate.
        </Text>
      </Container>
    </Box>
  );
};

export default GridPromoKlmSEP26;
